/**
 * Module-level async backup create progress (survives leaving Backups section).
 * Polls GET /backups/jobs/:id/ for terminal status; animates stages locally
 * (same soft UX as the old modal) while status is queued/running.
 */
import { toast } from "sonner";
import { backupAPI, backupScheduleAPI } from "@/lib/api";
import { renderProgressToast as renderSharedProgressToast } from "@/components/ui/progress-toast";

const TOAST_ID = "backup-create-progress";
const POLL_MS = 1200;
const TICK_MS = 200;
const CREATE_STAGE_MS = 4500;

/** Soft staged UX (not real byte %). Caps at 90% until job succeeds. */
const CREATE_STAGES = [
  { label: "Preparando…", targetPct: 18 },
  { label: "Dump de base de datos…", targetPct: 48 },
  { label: "Empaquetando archivos…", targetPct: 78 },
  { label: "Finalizando…", targetPct: 90 },
] as const;

type CompleteListener = (result: {
  ok: boolean;
  kind: "manual" | "scheduled";
  message?: string;
}) => void;

let activeJobId: string | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let animTimer: ReturnType<typeof setInterval> | null = null;
let listeners = new Set<CompleteListener>();

let displayPct = 0;
let stageIdx = 0;
let startedAt = 0;
let animDone = false;
let toastTitle = "Creando backup…";

export function isBackupCreateInFlight(): boolean {
  return activeJobId != null;
}

export function onBackupCreateComplete(cb: CompleteListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notifyComplete(result: {
  ok: boolean;
  kind: "manual" | "scheduled";
  message?: string;
}) {
  listeners.forEach((cb) => {
    try {
      cb(result);
    } catch {
      /* ignore listener errors */
    }
  });
}

function stopPolling() {
  if (pollTimer != null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function stopAnim() {
  if (animTimer != null) {
    clearInterval(animTimer);
    animTimer = null;
  }
}

function stageLabelForPct(pct: number, idx: number): string {
  if (pct >= 100) return "¡Completado!";
  return CREATE_STAGES[idx]?.label ?? "Creando backup…";
}

function renderProgressToast(pct: number, stage: string, title = toastTitle) {
  renderSharedProgressToast(TOAST_ID, pct, stage, title);
}

function startStageAnim(title: string) {
  stopAnim();
  toastTitle = title;
  startedAt = Date.now();
  displayPct = 4;
  stageIdx = 0;
  animDone = false;
  renderProgressToast(4, CREATE_STAGES[0].label, title);

  animTimer = setInterval(() => {
    if (animDone) return;
    const elapsed = Date.now() - startedAt;
    stageIdx = Math.min(
      Math.floor(elapsed / CREATE_STAGE_MS),
      CREATE_STAGES.length - 1,
    );
    const target = CREATE_STAGES[stageIdx].targetPct;
    displayPct = displayPct + Math.max(0.25, (target - displayPct) * 0.1);
    if (displayPct > target) displayPct = target;
    const pct = Math.min(Math.round(displayPct), 90);
    renderProgressToast(pct, stageLabelForPct(pct, stageIdx), title);
  }, TICK_MS);
}

async function finishSuccess(
  kind: "manual" | "scheduled",
  message?: string,
) {
  animDone = true;
  stopAnim();
  displayPct = 100;
  stageIdx = CREATE_STAGES.length - 1;
  renderProgressToast(100, "¡Completado!", toastTitle);
  await new Promise((r) => setTimeout(r, 450));
  toast.dismiss(TOAST_ID);
  toast.success(kind === "scheduled" ? "Backup programado listo" : "Backup creado exitosamente", {
    position: "bottom-right",
  });
  notifyComplete({ ok: true, kind, message });
}

async function pollOnce(
  jobId: string,
  title: string,
  kind: "manual" | "scheduled",
  errStreak = 0,
) {
  try {
    const job = await backupAPI.getJob(jobId);
    const st = job.status;
    // Ignore BE progress/stage while running — FE timer owns the bar.
    if (st === "queued" || st === "running") {
      pollTimer = setTimeout(() => void pollOnce(jobId, title, kind, 0), POLL_MS);
      return;
    }

    activeJobId = null;
    stopPolling();

    if (st === "success") {
      await finishSuccess(kind, job.message);
      return;
    }

    animDone = true;
    stopAnim();
    toast.dismiss(TOAST_ID);

    if (st === "skipped") {
      toast.message(job.message || "Backup omitido (lock ocupado)", { position: "bottom-right" });
      notifyComplete({ ok: false, kind, message: job.message });
      return;
    }
    toast.error(job.message || "Error creando backup", { position: "bottom-right" });
    notifyComplete({ ok: false, kind, message: job.message });
  } catch (e: unknown) {
    const msg =
      e && typeof e === "object" && "message" in e
        ? String((e as { message?: string }).message)
        : "Error consultando estado del backup";
    const next = errStreak + 1;
    if (next >= 8) {
      activeJobId = null;
      stopPolling();
      animDone = true;
      stopAnim();
      toast.dismiss(TOAST_ID);
      toast.error(`No se pudo seguir el progreso del backup: ${msg}`, { position: "bottom-right" });
      notifyComplete({ ok: false, kind, message: msg });
      return;
    }
    pollTimer = setTimeout(() => void pollOnce(jobId, title, kind, next), POLL_MS * 2);
    toast.custom(
      () => (
        <div className="pointer-events-auto w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-amber-200 bg-white p-3 shadow-lg dark:border-amber-900 dark:bg-slate-900">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Reintentando estado… {msg}</p>
        </div>
      ),
      { id: TOAST_ID, duration: Infinity, position: "bottom-right" },
    );
  }
}

/** Track an already-accepted job (manual create or schedule run-now). */
export function trackBackupJob(opts: {
  jobId: string;
  kind?: "manual" | "scheduled";
  title?: string;
}): boolean {
  if (activeJobId) {
    return false;
  }
  const kind = opts.kind ?? "manual";
  const title =
    opts.title ??
    (kind === "scheduled" ? "Backup programado en curso…" : "Creando backup…");
  activeJobId = opts.jobId;
  stopPolling();
  startStageAnim(title);
  void pollOnce(opts.jobId, title, kind);
  return true;
}

/** POST create + track toast. Returns false if another job is already tracked. */
export async function startManualBackupWithToast(label?: string): Promise<{
  started: boolean;
  jobId?: string;
}> {
  if (activeJobId) {
    toast.message("Ya hay un backup en progreso", { position: "bottom-right" });
    return { started: false };
  }
  const res = await backupAPI.create(label);
  const jobId = res.job_id || res.jobId;
  if (!jobId) {
    // Sync fallback (unlikely)
    if (res.status === "success") {
      toast.success("Backup creado exitosamente", { position: "bottom-right" });
      notifyComplete({ ok: true, kind: "manual" });
      return { started: true };
    }
    throw new Error("El servidor no devolvió job_id");
  }
  trackBackupJob({ jobId, kind: "manual" });
  return { started: true, jobId };
}

export async function startScheduleRunNowWithToast(scheduleId: number): Promise<{
  started: boolean;
  jobId?: string;
}> {
  if (activeJobId) {
    toast.message("Ya hay un backup en progreso", { position: "bottom-right" });
    return { started: false };
  }
  const res = await backupScheduleAPI.runNow(scheduleId);
  const jobId = res.job_id || res.jobId;
  if (!jobId) {
    // Sync response shape
    const status = res.last_status || res.lastStatus || res.schedule?.last_status;
    if (status === "success") {
      toast.success("Backup programado ejecutado", { position: "bottom-right" });
      notifyComplete({ ok: true, kind: "scheduled" });
      return { started: true };
    }
    throw new Error("El servidor no devolvió job_id");
  }
  trackBackupJob({ jobId, kind: "scheduled", title: "Backup programado en curso…" });
  return { started: true, jobId };
}
